import { useLayoutEffect, useState } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

/** App 0.7.0's ViewThatFits: retain the heading's intrinsic size, then move all actions together. */
export function useCompactRootHeader(header: HTMLElement | null, compact: boolean) {
  useLayoutEffect(() => {
    if (!header || !compact) return;
    let marked = new Set<HTMLElement>();
    let resize: ResizeObserver | null = null;
    const clear = (element: HTMLElement) => {
      for (const key of ['data-shell-header-heading', 'data-shell-compact-leading', 'data-shell-header-action', 'data-shell-header-spacer']) element.removeAttribute(key);
      element.style.removeProperty('--shell-action-column');
    };
    const measure = () => {
      const title = header.querySelector<HTMLElement>('h1,h2,[role="heading"]');
      if (!title) return;
      let heading = title;
      while (heading.parentElement && heading.parentElement !== header) heading = heading.parentElement;
      const children = [...header.children].filter((child): child is HTMLElement => child instanceof HTMLElement);
      for (const old of marked) if (!children.includes(old)) { resize?.unobserve(old); clear(old); }
      marked = new Set(children);
      for (const child of children) resize?.observe(child);
      let actions = 0;
      for (const child of children) {
        clear(child);
        if (child === heading) child.setAttribute('data-shell-header-heading', '');
        else if (child.matches('.shell-header-account,[data-testid="section-me"],[data-testid="navigation-back"]')) child.setAttribute('data-shell-compact-leading', '');
        else if (child.matches('.shell-toolbar-spacer')) child.setAttribute('data-shell-header-spacer', '');
        else {
          child.setAttribute('data-shell-header-action', '');
          child.style.setProperty('--shell-action-column', String(3 + actions++));
        }
      }
      header.style.setProperty('--shell-header-actions', String(Math.max(1, actions)));
      header.dataset.shellHeaderRows = 'one';
      const right = header.getBoundingClientRect().right - parseFloat(getComputedStyle(header).paddingRight || '0');
      const overflow = children.some(child => getComputedStyle(child).display !== 'none' && child.getBoundingClientRect().right > right + 1);
      header.dataset.shellHeaderRows = overflow ? 'two' : 'one';
    };
    header.setAttribute('data-shell-compact-header', '');
    measure();
    resize = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    resize?.observe(header);
    for (const child of marked) resize?.observe(child);
    // Text/locale changes can alter intrinsic widths without changing the outer width.
    const mutation = new MutationObserver(measure);
    mutation.observe(header, { childList: true, subtree: true, characterData: true });
    window.addEventListener('resize', measure);
    document.fonts?.addEventListener('loadingdone', measure);
    return () => {
      resize?.disconnect(); mutation.disconnect(); window.removeEventListener('resize', measure);
      document.fonts?.removeEventListener('loadingdone', measure);
      header.removeAttribute('data-shell-compact-header'); delete header.dataset.shellHeaderRows;
      header.style.removeProperty('--shell-header-actions'); for (const child of marked) clear(child);
    };
  }, [header, compact]);
}

export function CompactRootHeader({ compact, ...props }: ComponentPropsWithoutRef<'header'> & { compact: boolean }) {
  const [header, setHeader] = useState<HTMLElement | null>(null);
  useCompactRootHeader(header, compact);
  return <header {...props} ref={setHeader} />;
}
